import type { ApiErrorCode } from '@grc/types';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class ApiRequestError extends Error {
  readonly code: ApiErrorCode | 'network_error';
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(
    code: ApiRequestError['code'],
    message: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

/** Fetch a /api/v1 endpoint with session-cookie auth and normalized errors. */
export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const url = new URL(`${BASE}/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }

  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers,
      body,
      signal: opts.signal,
    });
  } catch (err) {
    throw new ApiRequestError('network_error', 'Could not reach the server', 0, {
      cause: String(err),
    });
  }

  if (!res.ok) {
    let code: ApiRequestError['code'] = 'internal';
    let message = `Request failed (${res.status})`;
    let details: Record<string, unknown> | undefined;
    try {
      const parsed = (await res.json()) as {
        error?: { code?: string; message?: string; details?: Record<string, unknown> };
      };
      if (parsed.error) {
        code = (parsed.error.code as ApiErrorCode) ?? code;
        message = parsed.error.message ?? message;
        details = parsed.error.details;
      }
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiRequestError(code, message, res.status, details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Absolute URL for browser-navigated endpoints (downloads, OAuth login). */
export function apiUrl(path: string): string {
  return `${BASE}/api/v1${path}`;
}
